import React, { FC, useContext } from 'react';
import { HtcCardWrapper, HtcCard } from '../../shared/HtcCardComponent';
import { IDepartment, NewIncotermId } from '../../models/masterdata';
import { useTranslation } from 'react-i18next';
import { Button, Input } from 'antd';
import _ from 'lodash';

import FormBox, { SearchBox } from '../../shared/components/FormBox/FormBox';
import CheckableTagGroup, { ICheckableTag } from '../../shared/components/CheckableTagGroup/CheckableTagGroup';
import { NewAction } from '../../shared/components/CommandBar/CommandBar';
import ContentWrapper from '../../shared/components/ContentWrapper/ContentWrapper';
import { UserInfoCtx, IUserInfoCtx } from '../../UserInfoContext';
import { useHistory } from 'react-router';
import { TFunction } from 'i18next';
import { Table } from '../../shared/components/Table/Table';
import { MasterdataHeader } from '../../shared/components/MasterdataHeader/MasterdataHeader';
import { CommandBarV2 } from '../../shared/components/CommandBar/CommandBarV2';
import { EditOutlined } from '@ant-design/icons';

const navPaths = ['navigation.masterdata', 'navigation.departments'];
export const DepartmentList: FC<IDepartmentListProps> = ({ loading, departments, setSearchText, searchText, departmentTypeTagOptions, setFilterDepartmentTypes, filterDepartmentTypes }) => {
  const [t] = useTranslation();
  const history = useHistory();
  const ctx = useContext(UserInfoCtx);

  const create = React.useCallback(() => history.push('/departments/' + NewIncotermId), [history]);
  const edit = React.useCallback((id: string) => history.push('/departments/' + id), [history]);
  const columns = React.useMemo(() => createColumns(t, ctx, edit), [ctx, edit, t]);
  const onSearch = (e: any) => setSearchText(e.target.value);

  return (
    <ContentWrapper>
      <MasterdataHeader>
        <CommandBarV2 paths={navPaths}>
          <NewAction text="masterdata.departments.newDepartment" onClick={create} />
        </CommandBarV2>
      </MasterdataHeader>
      <HtcCardWrapper>
        <HtcCard>
          <SearchBox>
            <Input placeholder={t('masterdata.departments.searchText')} allowClear onChange={onSearch} value={searchText} />
            <CheckableTagGroup
              isFilter={true}
              title={t('masterdata.departments.departmentType')}
              tags={departmentTypeTagOptions}
              defaultSelected={filterDepartmentTypes}
              onSelectedTagsChange={(s) => setFilterDepartmentTypes(_.map(s, (tag) => tag.tag))}
            />
          </SearchBox>
        </HtcCard>
        <HtcCard>
          <FormBox title={t('common.results')} flex contentflex>
            <Table rowKey="id" columns={columns} dataSource={departments} loading={loading} />
          </FormBox>
        </HtcCard>
      </HtcCardWrapper>
    </ContentWrapper>
  );
};

function createColumns(t: TFunction, ctx: IUserInfoCtx, onEdit: (id: string) => void) {
  return [
    {
      title: t('masterdata.departments.name'),
      dataIndex: 'name',
      key: 'name',
      width: 250,
      render: (text: string, record: IDepartment) => ctx.t(record.name),
    },
    {
      title: t('masterdata.departments.abbreviation'),
      dataIndex: 'abbreviation',
      key: 'abbreviation',
      width: 250,
      render: (text: string, record: IDepartment) => ctx.t(record.abbreviation),
    },
    {
      title: t('masterdata.departments.departmentType'),
      dataIndex: 'type',
      key: 'type',
      width: 300,
      render: (text: string) => <span>{t('masterdata.departments.types.' + text)}</span>,
    },

    {
      title: 'Actions',
      key: 'actions',
      render: (text: string, record: IDepartment) => <Button type="primary" shape="circle" size="small" icon={<EditOutlined />} onClick={() => onEdit(record.id)} />,
      width: 100,
    },
  ];
}

interface IDepartmentListProps {
  departments: IDepartment[];
  loading: boolean;
  searchText: string;
  setSearchText: (text: string) => void;
  filterDepartmentTypes: string[];
  setFilterDepartmentTypes: (tags: string[]) => void;
  departmentTypeTagOptions: ICheckableTag[];
}
